// Readoo API client — JWT Bearer token in localStorage (per WEB_MIGRATION.md contract).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const TOKEN_KEY = "readoo_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}
export function setToken(t) {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch {}
}

export async function apiFetch(path, opts = {}) {
  const { auth = true, method = "GET", body, headers, ...rest } = opts;
  const token = auth ? getToken() : "";
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    ...rest,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers || {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const detail = (data && data.detail) || (typeof data === "string" ? data : "Errore di rete");
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return data;
}

// ---- Public content ----
export const getArticles = (params = {}) => {
  const q = new URLSearchParams();
  if (params.category) q.set("category", params.category);
  if (params.source_id) q.set("source_id", params.source_id);
  if (params.limit) q.set("limit", params.limit);
  if (params.skip) q.set("skip", params.skip);
  if (params.fresh_only === false) q.set("fresh_only", "false");
  return apiFetch(`/articles?${q.toString()}`, { auth: false });
};
export const getArticle = (id) => apiFetch(`/articles/${id}`, { auth: false });
export const getTopStory = (category) => apiFetch(`/top-story?category=${encodeURIComponent(category)}`, { auth: false });
export const getHomeFeed = () => apiFetch(`/home-feed`, { auth: false });
export const getArticleBySlug = (category, slug) => apiFetch(`/articles/by-slug/${encodeURIComponent(category)}/${encodeURIComponent(slug)}`, { auth: false });
export const getRelated = (id, limit = 5) => apiFetch(`/articles/${id}/related?limit=${limit}`, { auth: false });
export const getCategories = () => apiFetch(`/categories`, { auth: false });
// ---- Interessi / feed personalizzato "Per te" ----
export const getInterests = () => apiFetch(`/me/interests`, { auth: true });
export const setInterests = (categories, tags = []) => apiFetch(`/me/interests`, { method: "PUT", auth: true, body: { categories, tags } });
export const getForYou = (params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", params.limit);
  if (params.skip) q.set("skip", params.skip);
  if (params.focus) q.set("focus", params.focus);
  return apiFetch(`/for-you?${q.toString()}`, { auth: true });
};
export const getTagFeed = (tag, params = {}) => {
  const q = new URLSearchParams();
  if (params.limit) q.set("limit", params.limit);
  if (params.skip) q.set("skip", params.skip);
  return apiFetch(`/tags/${tag}?${q.toString()}`, { auth: false });
};
export const getCouponOfDay = () => apiFetch(`/coupon-of-day`, { auth: true });
export const getCoupons = () => apiFetch(`/coupons`, { auth: true });
export const getCouponsPreview = () => apiFetch(`/coupons/preview`);
export const getBreaking = () => apiFetch(`/breaking-news`, { auth: false });
export const getTrending = (limit = 5) => apiFetch(`/trending?limit=${limit}`, { auth: false });
export const getHoroscopeToday = () => apiFetch(`/horoscope/today`, { auth: false });
export const getHoroscopeSign = (key) => apiFetch(`/horoscope/sign/${key}`, { auth: false });
export const getAskSuggestions = (id) => apiFetch(`/articles/${id}/ask/suggestions`, { auth: false });
export const getAskBox = (id, bid) => apiFetch(`/articles/${id}/ask/box?bid=${encodeURIComponent(bid || "")}`);
export const askReadoo = (id, question, browser_id = "", history = []) => apiFetch(`/articles/${id}/ask`, { method: "POST", body: { question, browser_id, history } });

// ---- Auth ----
export const apiRegister = (payload) => apiFetch(`/auth/register`, { method: "POST", auth: false, body: payload });
export const apiLogin = (payload) => apiFetch(`/auth/login`, { method: "POST", auth: false, body: payload });
export const apiGoogleSession = (session_id, consents = null) => apiFetch(`/auth/google/session`, { method: "POST", auth: false, body: { session_id, consents } });
export const apiChangePassword = (body) => apiFetch(`/me/password`, { method: "POST", body });
export const apiGoogleConfig = () => apiFetch(`/auth/google/config`, { auth: false });
export const apiGoogleOAuth = (code, redirect_uri, consents = null) => apiFetch(`/auth/google/oauth`, { method: "POST", auth: false, body: { code, redirect_uri, consents } });
export const apiForgotPassword = (email) => apiFetch(`/auth/password/forgot`, { method: "POST", auth: false, body: { email } });
export const apiResetCheck = (token) => apiFetch(`/auth/password/reset/check`, { method: "POST", auth: false, body: { token } });
export const apiResetPassword = (token, new_password) => apiFetch(`/auth/password/reset`, { method: "POST", auth: false, body: { token, new_password } });
export const adminGetBrevo = () => apiFetch(`/admin/brevo`);
export const adminSetBrevoKey = (api_key) => apiFetch(`/admin/brevo`, { method: "POST", body: { api_key } });
export const adminTestBrevo = (to) => apiFetch(`/admin/brevo/test`, { method: "POST", body: { to } });
export const apiSetConsents = (consents) => apiFetch(`/me/consents`, { method: "POST", auth: true, body: consents });
export const adminConsentsCsvUrl = () => `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/consents.csv`;
export const apiMe = () => apiFetch(`/auth/me`, { auth: true });
export const apiLogout = () => apiFetch(`/auth/logout`, { method: "POST", auth: true });
export const apiUpdateZodiac = (zodiac_sign) => apiFetch(`/auth/me/zodiac`, { method: "PATCH", auth: true, body: { zodiac_sign } });

// ---- Gamification / engagement ----
export const apiReadStart = (article_id) => apiFetch(`/read/start`, { method: "POST", auth: true, body: { article_id } });
export const apiReadComplete = (payload) => apiFetch(`/read/complete`, { method: "POST", auth: true, body: payload });
export const apiOpenOriginal = (id) => apiFetch(`/articles/${id}/open-original`, { method: "POST", auth: true });
export const apiCheckIn = () => apiFetch(`/streak/check-in`, { method: "POST", auth: true });
export const getMissionsToday = () => apiFetch(`/missions/today`, { auth: true });
export const apiPointsHistory = () => apiFetch(`/points/history`, { auth: true });
export const apiValidateRedoo = () => apiFetch(`/redoo/validate`, { method: "POST", auth: true });
export const getLevels = () => apiFetch(`/levels`, { auth: false });
export const getMyLevel = () => apiFetch(`/levels/me`, { auth: true });
export const getPushPublicKey = () => apiFetch(`/push/public-key`, { auth: false });
export const adminPushOverview = () => apiFetch(`/admin/push/overview`, { auth: true });
export const adminCreateRecurring = (p) => apiFetch(`/admin/push/recurring`, { method: "POST", auth: true, body: p });
export const adminUpdateRecurring = (id, p) => apiFetch(`/admin/push/recurring/${id}`, { method: "PATCH", auth: true, body: p });
export const adminDeleteRecurring = (id) => apiFetch(`/admin/push/recurring/${id}`, { method: "DELETE", auth: true });
export const adminPushSend = (p) => apiFetch(`/admin/push/send`, { method: "POST", auth: true, body: p });
export const adminDeleteScheduled = (id) => apiFetch(`/admin/push/scheduled/${id}`, { method: "DELETE", auth: true });
export const adminGetLlm = () => apiFetch(`/admin/llm`, { auth: true });
export const adminSetLlm = (p) => apiFetch(`/admin/llm`, { method: "POST", auth: true, body: p });
export const getSoftAsk = () => apiFetch(`/push/softask`, { auth: false });
export const adminSetSoftAsk = (p) => apiFetch(`/admin/push/softask`, { method: "POST", auth: true, body: p });
export const adminGetLevels = () => apiFetch(`/admin/levels`, { auth: true });
export const adminSetLevels = (levels) => apiFetch(`/admin/levels`, { method: "POST", auth: true, body: { levels } });
export const getLeaderboard = (limit = 20) => apiFetch(`/leaderboard?limit=${limit}`, { auth: false });
export const getRewards = () => apiFetch(`/rewards`, { auth: false });
export const apiRedeem = (reward_id) => apiFetch(`/rewards/redeem`, { method: "POST", auth: true, body: { reward_id } });
export const apiMyRedemptions = () => apiFetch(`/rewards/my-redemptions`, { auth: true });
export const getQuizToday = () => apiFetch(`/quiz/today`, { auth: true });
export const apiQuizAnswer = (quiz_id, answer_index) => apiFetch(`/quiz/answer`, { method: "POST", auth: true, body: { quiz_id, answer_index } });

// ---- Settings (public flags) ----
export const getSettings = () => apiFetch(`/settings`, { auth: false });
export const getNewsToday = () => apiFetch(`/news-today`, { auth: false });

// ---- Admin ----
export const adminStats = () => apiFetch(`/admin/stats`, { auth: true });
export const adminUsers = () => apiFetch(`/admin/users`, { auth: true });
export const adminUserStats = (id) => apiFetch(`/admin/users/${id}/stats`, { auth: true });
export const adminDeleteUser = (id) => apiFetch(`/admin/users/${id}`, { method: "DELETE", auth: true });
export const adminArticles = ({ q = "", category = "", status = "all", page = 1, page_size = 10 } = {}) =>
  apiFetch(`/admin/articles?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}&status=${status}&page=${page}&page_size=${page_size}`, { auth: true });
export const adminDeleteArticle = (id) => apiFetch(`/admin/articles/${id}`, { method: "DELETE", auth: true });
export const adminBulkDeleteArticles = (article_ids) =>
  apiFetch(`/admin/articles/bulk-delete`, { method: "POST", auth: true, body: { article_ids } });
export const adminCleanupDrafts = () =>
  apiFetch(`/admin/articles/cleanup-drafts`, { method: "POST", auth: true });
export const adminSetRole = (id, role) => apiFetch(`/admin/users/${id}/role`, { method: "PATCH", auth: true, body: { role } });
export const adminRewards = () => apiFetch(`/admin/rewards`, { auth: true });
export const adminCreateReward = (payload) => apiFetch(`/admin/rewards`, { method: "POST", auth: true, body: payload });
export const adminUpdateReward = (id, payload) => apiFetch(`/admin/rewards/${id}`, { method: "PATCH", auth: true, body: payload });
export const adminDeleteReward = (id) => apiFetch(`/admin/rewards/${id}`, { method: "DELETE", auth: true });
export const adminSignupSources = (days = 30) => apiFetch(`/admin/signup-sources?days=${days}`, { auth: true });
export const adminTimeseries = (days = 7) => apiFetch(`/admin/stats/timeseries?days=${days}`, { auth: true });
export const adminRedemptions = () => apiFetch(`/admin/redemptions`, { auth: true });
export const adminFulfill = (id) => apiFetch(`/admin/redemptions/${id}/fulfill`, { method: "POST", auth: true });
export const adminGenerateHoroscope = (force = false) => apiFetch(`/admin/horoscope/generate?force=${force}`, { method: "POST", auth: true });
export const adminGetAskWeb = () => apiFetch(`/admin/settings/ask-web`);
export const adminSetAskWeb = (enabled) => apiFetch(`/admin/settings/ask-web`, { method: "POST", body: { enabled } });
export const adminSetLogin = (enabled) => apiFetch(`/admin/settings/login`, { method: "POST", auth: true, body: { enabled } });
export const adminSetGamification = (enabled) => apiFetch(`/admin/settings/gamification`, { method: "POST", auth: true, body: { enabled } });
export const adminGetGeneration = () => apiFetch(`/admin/settings/generation`, { auth: true });
export const adminSetGeneration = (paused) => apiFetch(`/admin/settings/generation`, { method: "POST", auth: true, body: { enabled: paused } });
export const adminGetCategoryRefresh = () => apiFetch(`/admin/category-refresh`, { auth: true });
export const adminSetCategoryRefresh = (intervals) => apiFetch(`/admin/category-refresh`, { method: "POST", auth: true, body: { intervals } });
export const getSources = () => apiFetch(`/sources`, { auth: true });
export const createSource = (payload) => apiFetch(`/sources`, { method: "POST", auth: true, body: payload });
export const deleteSource = (id) => apiFetch(`/sources/${id}`, { method: "DELETE", auth: true });
export const syncSource = (id) => apiFetch(`/sources/${id}/sync`, { method: "POST", auth: true });
export const syncAllSources = () => apiFetch(`/sources/sync-all`, { method: "POST", auth: true });

// ---- Ad Inserter (pubblicità / script di terze parti) ----
export const getAdblocks = () => apiFetch(`/adblocks`, { auth: false });
export const adminAdblocks = () => apiFetch(`/admin/adblocks`, { auth: true });
export const adminCreateAdblock = (payload) => apiFetch(`/admin/adblocks`, { method: "POST", auth: true, body: payload });
export const adminUpdateAdblock = (id, payload) => apiFetch(`/admin/adblocks/${id}`, { method: "PUT", auth: true, body: payload });
export const adminDeleteAdblock = (id) => apiFetch(`/admin/adblocks/${id}`, { method: "DELETE", auth: true });
